import { supabase } from '../lib/supabase';

export type LineupSource = 'depth_chart' | 'manual';

export interface Game {
  id: string;
  team_id: string;
  date: string;
  opponent_name: string;
  home_away: 'home' | 'away';
  location?: string;
  game_time?: string;
  season?: string;
  lineup_source?: LineupSource;
  created_at: string;
  updated_at: string;
}

export interface GameLineupEntry {
  batting_order: number;
  player_id: string;
}

/**
 * Service for managing games
 */
class GameService {
  /**
   * Get all games for a team
   */
  async getGames(teamId: string): Promise<Game[]> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('team_id', teamId)
      .order('date', { ascending: false });

    if (error) {
      console.error('[GameService] Error fetching games:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a single game by ID
   */
  async getGame(gameId: string): Promise<Game | null> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('id', gameId)
      .single();

    if (error) {
      console.error('[GameService] Error fetching game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Create a new game
   */
  async createGame(game: Omit<Game, 'id' | 'created_at' | 'updated_at'>): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .insert({
        team_id: game.team_id,
        date: game.date,
        opponent_name: game.opponent_name,
        home_away: game.home_away,
        location: game.location || null,
        game_time: game.game_time || null,
        season: game.season || null,
        lineup_source: game.lineup_source || 'depth_chart',
      })
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error creating game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update an existing game
   */
  async updateGame(
    gameId: string,
    updates: Partial<Omit<Game, 'id' | 'team_id' | 'created_at' | 'updated_at'>>
  ): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update(updates)
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error updating game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a game
   */
  async deleteGame(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('games')
      .delete()
      .eq('id', gameId);

    if (error) {
      console.error('[GameService] Error deleting game:', error);
      throw error;
    }
  }

  /**
   * Get manual lineup for a game (when lineup_source === 'manual')
   */
  async getGameLineup(gameId: string): Promise<GameLineupEntry[]> {
    const { data, error } = await supabase
      .from('game_lineup')
      .select('batting_order, player_id')
      .eq('game_id', gameId)
      .order('batting_order', { ascending: true });

    if (error) {
      console.error('[GameService] Error fetching game lineup:', error);
      throw error;
    }
    return (data || []).map((row) => ({ batting_order: row.batting_order, player_id: row.player_id }));
  }

  /**
   * Set manual lineup for a game (replaces existing)
   */
  async setGameLineup(gameId: string, entries: GameLineupEntry[]): Promise<void> {
    const { error: deleteError } = await supabase.from('game_lineup').delete().eq('game_id', gameId);
    if (deleteError) {
      console.error('[GameService] Error clearing game lineup:', deleteError);
      throw deleteError;
    }
    if (entries.length === 0) return;
    const rows = entries.map((e) => ({ game_id: gameId, batting_order: e.batting_order, player_id: e.player_id }));
    const { error: insertError } = await supabase.from('game_lineup').insert(rows);
    if (insertError) {
      console.error('[GameService] Error saving game lineup:', insertError);
      throw insertError;
    }
  }
}

export const gameService = new GameService();

